import { buildApiUrl } from '@map/hooks/baseApi';
import { UserDocumentRequest, WithCancelToken } from '@map/types';
import useAction from '@shared/hooks/useAction';
import { keyBy } from '@shared/util/array';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Dispatch, Reducer } from 'redux';
import { createSelector } from 'reselect';

type State = Record<string, UserDocumentRequest>;

/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
const USER_DOCUMENTS_FETCHED = 'USER_DOCUMENTS_FETCHED' as const;
const USER_DOCUMENT_REQUEST_CREATED = 'USER_DOCUMENT_REQUEST_CREATED' as const;
const USER_DOCUMENT_REQUEST_UPDATED = 'USER_DOCUMENT_REQUEST_UPDATED' as const;
/* eslint-enable @typescript-eslint/no-unnecessary-type-assertion */

const actionCreators = {
  fetched: (data: UserDocumentRequest[]) => ({ type: USER_DOCUMENTS_FETCHED, data }),
  create: (data: UserDocumentRequest) => ({ type: USER_DOCUMENT_REQUEST_CREATED, data }),
  update: (id: number, data: Partial<UserDocumentRequest>) => ({
    type: USER_DOCUMENT_REQUEST_UPDATED,
    data: { ...data, id },
  }),
};

type Action = ReturnType<(typeof actionCreators)[keyof typeof actionCreators]>;

const userDocuments: Reducer<State, Action> = (state = {}, action) => {
  switch (action.type) {
    case USER_DOCUMENTS_FETCHED:
      return keyBy(
        action.data.map((doc) => ({ ...doc, notified: true })),
        ({ id }) => id,
      );
    case USER_DOCUMENT_REQUEST_CREATED:
    case USER_DOCUMENT_REQUEST_UPDATED:
      return { ...state, [action.data.id]: { ...state[action.data.id], ...action.data } };
    default:
      return state;
  }
};

export default userDocuments;

type RootState = { userDocuments: State };
const rootSelector = (state: RootState) => state.userDocuments;

const arraySelector = createSelector(rootSelector, (object) => Object.values(object));

export const fetchUserDocuments =
  ({ cancelToken }: WithCancelToken) =>
  async (dispatch: Dispatch) => {
    const res = await axios.get(buildApiUrl('user_documents'), { cancelToken });
    return dispatch(actionCreators.fetched(res.data as UserDocumentRequest[]));
  };
export const useUserDocuments = () => useSelector(arraySelector);

export const useCreateDocumentRequest = () => useAction(actionCreators.create);

export const useUpdateDocumentRequest = () => useAction(actionCreators.update);
